import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { buildTemplateFile, templateFileName } from 'modules/manager/domain/template-file';
import { useLazyMediaManagerTemplateQuery } from 'modules/manager/store/media-manager-api';
import { useCallback, useRef, useState } from 'react';
import { downloadTextFile } from 'shared/utils/download-text-file';

export interface ExportTemplatesOptions {
  /** Names a batch file: `<label>-templates-<date>…`. */
  label?: string;
  /**
   * The templates already carry their final `objects` — never read them again,
   * even when `objects` is empty. The editor passes its live canvas this way:
   * a cleared canvas must not turn into the saved version.
   */
  complete?: boolean;
}

export interface UseExportTemplatesResult {
  isExporting: boolean;
  /**
   * Downloads the templates as one file. Resolves `false` when a previous export
   * is still running; rejects when a template could not be read — the caller
   * shows the toast, and nothing is downloaded.
   */
  exportTemplates: (templates: readonly MediaManagerTemplate[], options?: ExportTemplatesOptions) => Promise<boolean>;
}

/**
 * The organization list is selected without `objects` (`LIST_FIELDS`) and the
 * mapper turns the missing field into `[]`, so a template with no objects is read
 * in full before it goes into the file. Presets already carry them — no request
 * for those. A saved empty canvas costs one harmless extra read; the editor opts
 * out with `complete`.
 */
function needsFullRead(template: MediaManagerTemplate): boolean {
  return template.objects.length === 0;
}

export function useExportTemplates(): UseExportTemplatesResult {
  const [readTemplate] = useLazyMediaManagerTemplateQuery();
  const [isExporting, setIsExporting] = useState(false);
  // Synchronous guard, as in `use-duplicate-template.ts`: two clicks in one tick
  // both see the stale `false` state before the control is disabled.
  const inFlight = useRef(false);

  const exportTemplates = useCallback(
    async (templates: readonly MediaManagerTemplate[], { label, complete = false }: ExportTemplatesOptions = {}): Promise<boolean> => {
      if (inFlight.current || templates.length === 0) return false;

      inFlight.current = true;
      setIsExporting(true);
      try {
        const full: MediaManagerTemplate[] = [];
        // One by one: a batch of multi-megabyte templates in parallel would
        // stall the tab for no gain.
        for (const template of templates) {
          full.push(!complete && needsFullRead(template) ? await readTemplate(template.id, true).unwrap() : template);
        }
        const file = buildTemplateFile(full, { origin: window.location.origin, exportedAt: new Date().toISOString() });
        downloadTextFile(JSON.stringify(file, null, 2), templateFileName(full, label));
        return true;
      } finally {
        inFlight.current = false;
        setIsExporting(false);
      }
    },
    [readTemplate],
  );

  return { isExporting, exportTemplates };
}
